// Land Intelligence skill — Pipeline Manager UI lands in Batch 8.
// Today: minimal landing page that exposes the Power Agent surface so
// it's testable end-to-end without waiting for the full Kanban + map.
//
// Brain: [skills/land-intelligence].
import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function LandIntelligence() {
  const [latitude, setLatitude] = useState('29.5');
  const [longitude, setLongitude] = useState('-104.4');
  const [targetMw, setTargetMw] = useState('200');
  const [parcelId, setParcelId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE}/land/analyze/power`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          target_mw: parseFloat(targetMw),
          parcel_id: parcelId || undefined,
        }),
      });
      if (!resp.ok) {
        throw new Error(`Analysis failed: ${resp.status} ${resp.statusText}`);
      }
      setResult(await resp.json());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 p-6 lg:p-10">
      <div className="mx-auto max-w-5xl">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Land Intelligence — Power Agent quick test
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-900">
            Power Solution Matrix
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">
            Run a one-shot Power Agent analysis against any lat/lon. Returns
            all six power pathways ranked by speed-to-MW and cost, plus a
            recommended phased deployment plan. For full Power+Water+
            convergence + Deal Economics, use the{' '}
            <a href="/land-intelligence/pipeline" className="font-semibold text-zinc-900 underline">
              Pipeline Manager
            </a>{' '}
            instead — that runs the full skill end-to-end and persists results.
          </p>
        </header>

        <section className="mb-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-500">
            Analyze a parcel
          </h2>
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-semibold text-zinc-600">
              Latitude
              <input
                type="number"
                step="0.0001"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold text-zinc-600">
              Longitude
              <input
                type="number"
                step="0.0001"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold text-zinc-600">
              Target MW
              <input
                type="number"
                step="1"
                value={targetMw}
                onChange={(e) => setTargetMw(e.target.value)}
                className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold text-zinc-600">
              Parcel ID (optional)
              <input
                type="text"
                value={parcelId}
                onChange={(e) => setParcelId(e.target.value)}
                placeholder="TX-PRESIDIO-12345"
                className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-500"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={runAnalysis}
            disabled={loading}
            className="mt-4 rounded-xl bg-zinc-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Analyzing…' : 'Run Power Agent'}
          </button>
        </section>

        {error && (
          <div className="mb-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {result && <PowerMatrixView matrix={result} />}

        <section className="mt-8 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-500">
            Pathway coverage
          </h2>
          <p className="mb-3 text-sm text-zinc-600">
            Power Agent pathways with confidence levels reflecting the data sources
            wired today. Higher confidence = more empirical, less heuristic.
          </p>
          <ul className="space-y-1 text-sm text-zinc-700">
            <li>✅ <strong>behind_meter</strong> · 70% — NREL NSRDB solar resource + ATB cost benchmarks</li>
            <li>✅ <strong>ppa</strong> · 65% — ISO/RTO queue snapshots + LBNL queue fallback</li>
            <li>✅ <strong>stranded</strong> · ~65% — EIA retiring assets <em>(geo data rebuild queued — see [skills/land-intelligence])</em></li>
            <li>✅ <strong>grid</strong> · 55% — OSM Overpass nearest substation + heuristic upgrade/cost bands</li>
            <li>✅ <strong>rapid_deploy</strong> · 50% — Equipment broker fixture data</li>
            <li>⏳ <strong>smr</strong> — stub (NRC + seismic + state-nuclear-enabled connectors deferred to Batch 11)</li>
          </ul>
          <p className="mt-3 text-xs text-zinc-500">
            Cooling Solution Matrix (Water Agent), Deal Economics, scheduled scans, and the
            full Pipeline Manager UI all ship and run end-to-end. See the brain page
            <code className="mx-1 rounded bg-zinc-100 px-1 py-0.5 text-zinc-700">[skills/land-intelligence]</code>
            for the current state and resume conditions.
          </p>
        </section>
      </div>
    </div>
  );
}

function PowerMatrixView({ matrix }) {
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <header className="mb-4 flex items-baseline justify-between">
        <h2 className="text-lg font-bold text-zinc-900">
          {matrix.parcel_id} — {matrix.target_mw} MW target
        </h2>
        <p className="text-xs text-zinc-500">
          confidence {(matrix.overall_confidence * 100).toFixed(0)}% · analyzed {new Date(matrix.analyzed_at).toLocaleString()}
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <RankPanel title="Speed rank — months to first MW" rows={matrix.speed_rank} unit="mo" />
        <RankPanel title="Cost rank — $/MW" rows={matrix.cost_rank} unit="$" />
      </div>

      <PhasedPlan plan={matrix.recommended_strategy} />

      {matrix.data_gaps?.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
            Data gaps ({matrix.data_gaps.length})
          </p>
          <ul className="space-y-1 text-sm text-amber-900">
            {matrix.data_gaps.map((g, i) => (
              <li key={i}>• {g}</li>
            ))}
          </ul>
        </div>
      )}

      {matrix.recommended_next_actions?.length > 0 && (
        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-emerald-800">
            Recommended next actions
          </p>
          <ul className="space-y-1 text-sm text-emerald-900">
            {matrix.recommended_next_actions.map((a, i) => (
              <li key={i}>• {a}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function RankPanel({ title, rows, unit }) {
  return (
    <div className="rounded-xl border border-zinc-200 p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm italic text-zinc-400">no pathway data yet</p>
      ) : (
        <ol className="space-y-1 text-sm">
          {rows.map((r) => (
            <li key={r.pathway} className="flex justify-between gap-4 text-zinc-700">
              <span>
                <strong>{r.rank}.</strong> {r.pathway}
              </span>
              <span className="font-mono text-zinc-500">
                {unit === '$' ? `$${formatNumber(r.value)}` : `${r.value.toFixed(0)} ${unit}`}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function PhasedPlan({ plan }) {
  if (!plan?.phases || plan.phases.length === 0) return null;
  return (
    <div className="mt-4 rounded-xl border border-zinc-200 p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Recommended phased deployment
      </h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">
            <th className="py-2">Phase</th>
            <th className="py-2">Pathway</th>
            <th className="py-2">Capacity</th>
            <th className="py-2">Window</th>
            <th className="py-2">Capex (mid)</th>
          </tr>
        </thead>
        <tbody>
          {plan.phases.map((p) => (
            <tr key={p.phase_number} className="border-b border-zinc-100 text-zinc-700">
              <td className="py-2 font-semibold">{p.phase_number}</td>
              <td className="py-2">{p.name}</td>
              <td className="py-2 font-mono">{p.capacity_mw.toFixed(0)} MW</td>
              <td className="py-2 font-mono text-zinc-500">m{p.start_month}–m{p.completion_month}</td>
              <td className="py-2 font-mono">${formatNumber(p.capex_usd?.mid)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-zinc-500">
        Total ${formatNumber(plan.total_cost_usd?.mid)} · first MW in {plan.time_to_first_mw_months} mo · full capacity in {plan.time_to_full_capacity_months} mo
      </p>
    </div>
  );
}

function formatNumber(n) {
  if (n == null) return '—';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}
